import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
    useRef,
} from "react";
import { ChartLockInfo, ChartLockStatus } from "intake_shared/src/types";
import {
    releaseChartLock,
    getChartLockInfo,
    subscribeToChartLock,
    lockChart,
} from "../store/chart_lock";
import { AclContext } from "./AclContext";
import { useToasts } from "react-toast-notifications";

interface ChartLockContextType {
    // Current chart lock state
    currentLockInfo: ChartLockInfo | null;

    // Actions
    releaseChartLock: (clientId: string) => Promise<boolean>;
    autoLockChart: (clientId: string) => Promise<boolean>;

    // Real-time subscription
    subscribeToChartLock: (clientId: string) => void;
    unsubscribeFromChartLock: () => void;
}

const ChartLockContext = createContext<ChartLockContextType | undefined>(undefined);

interface ChartLockProviderProps {
    children: ReactNode;
}

export const ChartLockProvider: React.FC<ChartLockProviderProps> = ({
    children,
}) => {
    const [currentLockInfo, setCurrentLockInfo] = useState<ChartLockInfo | null>(
        null
    );
    const [currentClientId, setCurrentClientId] = useState<string | null>(null);
    const unsubscribeRef = useRef<(() => void) | null>(null);
    const aclContext = useContext(AclContext);
    const authUser = aclContext?.authUser;
    const { addToast } = useToasts();

    const currentUserEmail = authUser?.user?.email;
    const currentUserName =
        authUser?.user?.displayName || currentUserEmail || "Unknown User";

    // Cleanup subscription on unmount
    useEffect(() => {
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, []);

    // Cleanup when user changes
    useEffect(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        setCurrentLockInfo(null);
        setCurrentClientId(null);
    }, [currentUserEmail]);

    const autoLockChartHandler = useCallback(
        async (clientId: string): Promise<boolean> => {
            if (!currentUserEmail) {
                return false;
            }

            // Check if we already have the lock
            if (
                currentLockInfo?.status === ChartLockStatus.LOCKED_BY_ME &&
                currentClientId === clientId
            ) {
                return true;
            }

            // Check if someone else has the lock
            const lockInfo = await getChartLockInfo(clientId, currentUserEmail);
            if (lockInfo.status === ChartLockStatus.LOCKED_BY_OTHER) {
                setCurrentLockInfo(lockInfo);
                setCurrentClientId(clientId);
                return false;
            }

            // Try to acquire lock
            try {
                const result = await lockChart(
                    clientId,
                    currentUserEmail,
                    currentUserName
                );

                if (result.success) {
                    setCurrentLockInfo({
                        status: ChartLockStatus.LOCKED_BY_ME,
                        lock: result.lock,
                        can_edit: true,
                    });
                    setCurrentClientId(clientId);
                    return true;
                } else {
                    return false;
                }
            } catch (error) {
                console.error("Error auto-locking chart:", error);
                return false;
            }
        },
        [currentUserEmail, currentUserName, currentLockInfo, currentClientId]
    );

    const releaseChartLockHandler = useCallback(
        async (clientId: string): Promise<boolean> => {
            if (!currentUserEmail) {
                return false;
            }

            try {
                const success = await releaseChartLock(clientId, currentUserEmail);

                if (success) {
                    setCurrentLockInfo({
                        status: ChartLockStatus.AVAILABLE,
                        can_edit: true,
                    });
                    setCurrentClientId(null);

                    if (unsubscribeRef.current) {
                        unsubscribeRef.current();
                        unsubscribeRef.current = null;
                    }

                    addToast("Chart lock released", { appearance: "success" });
                    return true;
                } else {
                    addToast("Failed to release chart lock", {
                        appearance: "error",
                    });
                    return false;
                }
            } catch (error) {
                console.error("Error releasing chart lock:", error);
                addToast("Failed to release chart lock", { appearance: "error" });
                return false;
            }
        },
        [currentUserEmail, addToast]
    );

    const subscribeToChartLockHandler = useCallback(
        (clientId: string) => {
            if (!currentUserEmail) {
                return;
            }

            // Unsubscribe from previous subscription
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }

            const newUnsubscribe = subscribeToChartLock(
                clientId,
                currentUserEmail,
                async (lockInfo: ChartLockInfo) => {
                    setCurrentLockInfo(lockInfo);
                    setCurrentClientId(clientId);
                }
            );

            unsubscribeRef.current = newUnsubscribe;
        },
        [currentUserEmail]
    );

    const unsubscribeFromChartLockHandler = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        setCurrentLockInfo(null);
        setCurrentClientId(null);
    }, []);

    const contextValue: ChartLockContextType = {
        currentLockInfo,
        releaseChartLock: releaseChartLockHandler,
        autoLockChart: autoLockChartHandler,
        subscribeToChartLock: subscribeToChartLockHandler,
        unsubscribeFromChartLock: unsubscribeFromChartLockHandler,
    };

    return (
        <ChartLockContext.Provider value={contextValue}>
            {children}
        </ChartLockContext.Provider>
    );
};

export const useChartLock = (): ChartLockContextType => {
    const context = useContext(ChartLockContext);
    if (context === undefined) {
        throw new Error("useChartLock must be used within a ChartLockProvider");
    }
    return context;
};

export default ChartLockContext;
